import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { devLog, devWarn, devError } from '@/lib/logger';

interface UserProfile {
  id: number;
  nome: string;
  email: string;
  ativo: boolean;
  operadorId: number | null;
  senha_temporaria: boolean;
}

interface AuthState {
  initializing: boolean;
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  roles: string[];
  profileError: boolean;
}

interface AuthContextType extends AuthState {
  isAdmin: boolean;
  isOperador: boolean;
  isVendedor: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const toNumberId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    // Supabase pode retornar bigint como string; aceitamos apenas strings numéricas
    if (/^\d+$/.test(value)) return Number(value);
  }
  return null;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const queryClient = useQueryClient();
  
  const [state, setState] = useState<AuthState>({
    initializing: true,
    session: null,
    user: null,
    profile: null,
    roles: [],
    profileError: false,
  });
  
  // Flag adicional para controlar estado de loading durante autenticação
  const [authLoading, setAuthLoading] = useState(false);

  // Anti-loop refs: track which authId we last fetched, and if we're currently fetching
  const lastFetchedAuthIdRef = useRef<string | null>(null);
  const fetchingRef = useRef<boolean>(false);

  const fetchProfileAndRoles = async (authId: string, attempt = 1): Promise<void> => {
    // Guard: don't re-fetch if we already fetched this authId or are currently fetching
    if (lastFetchedAuthIdRef.current === authId || fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;

    try {
      // Fetch profile from public.users (incluindo operador_id para unidade operacional)
      const { data: profileData, error: profileError } = await supabase
        .from('users')
        .select('id, nome, email, ativo, operador_id, senha_temporaria')
        .eq('auth_id', authId)
        .single();

      if (profileError || !profileData) {
        if (attempt < 3) {
          // Exponential retry: 500ms, 1000ms, 2000ms
          fetchingRef.current = false;
          await new Promise(resolve => setTimeout(resolve, 500 * attempt));
          return fetchProfileAndRoles(authId, attempt + 1);
        }

        // All retries failed
        setState(prev => ({
          ...prev,
          profileError: true,
          initializing: false,
        }));
        fetchingRef.current = false;
        return;
      }

      const coercedId = toNumberId((profileData as any).id);
      const coercedOperadorId = toNumberId((profileData as any).operador_id);

      // Se não conseguir coagir IDs para número, tratamos como erro de perfil (evita UUID indo pra user_id)
      if (!coercedId) {
        setState(prev => ({
          ...prev,
          profileError: true,
          initializing: false,
        }));
        fetchingRef.current = false;
        return;
      }

      // Fetch roles from public.user_roles
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', coercedId);

      const roles = rolesData?.map(r => r.role) || [];

      // Mark as successfully fetched
      lastFetchedAuthIdRef.current = authId;

      setState(prev => ({
        ...prev,
        profile: {
          id: coercedId,
          nome: (profileData as any).nome,
          email: (profileData as any).email,
          ativo: (profileData as any).ativo,
          operadorId: coercedOperadorId,
          senha_temporaria: (profileData as any).senha_temporaria ?? false,
        },
        roles,
        profileError: false,
        initializing: false,
      }));
    } catch (error) {
      if (attempt < 3) {
        fetchingRef.current = false;
        await new Promise(resolve => setTimeout(resolve, 500 * attempt));
        return fetchProfileAndRoles(authId, attempt + 1);
      }

      setState(prev => ({
        ...prev,
        profileError: true,
        initializing: false,
      }));
    } finally {
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    // 1. FIRST: Register onAuthStateChange listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        devLog('[Auth]', 'onAuthStateChange:', event, newSession?.user?.id ?? 'no user');
        
        // Update session/user synchronously
        setState(prev => ({
          ...prev,
          session: newSession,
          user: newSession?.user ?? null,
        }));

        if (newSession?.user) {
          const authId = newSession.user.id;
          // Only fetch if authId changed
          if (lastFetchedAuthIdRef.current !== authId) {
            // Defer to avoid Supabase deadlock
            setTimeout(() => {
              fetchProfileAndRoles(authId);
            }, 0);
          }
          
          // ============= RESILIÊNCIA DE SESSÃO =============
          // Invalidar cache crítico quando sessão é restaurada ou token renovado
          // Isso força refetch de dados operacionais após reconexão
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            devLog('[Auth]', '🔄 Invalidando cache crítico após:', event);
            queryClient.invalidateQueries({ queryKey: ['perfil'] });
            queryClient.invalidateQueries({ queryKey: ['pedidos'] });
            queryClient.invalidateQueries({ queryKey: ['cross-sell-profit'] });
            queryClient.invalidateQueries({ queryKey: ['vendas-produto'] });
            queryClient.invalidateQueries({ queryKey: ['cardapio'] });
            queryClient.invalidateQueries({ queryKey: ['clientes'] });
            queryClient.invalidateQueries({ queryKey: ['fiados'] });
            queryClient.invalidateQueries({ queryKey: ['fluxo_caixa'] });
            queryClient.invalidateQueries({ queryKey: ['whatsapp-instance-config'] });
          }
        } else {
          // Logged out: reset state
          lastFetchedAuthIdRef.current = null;
          setState(prev => ({
            ...prev,
            profile: null,
            roles: [],
            profileError: false,
            initializing: false,
          }));
        }
      }
    );

    // 2. THEN: Get existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
      }));

      if (session?.user) {
        const authId = session.user.id;
        if (lastFetchedAuthIdRef.current !== authId) {
          fetchProfileAndRoles(authId);
        }
      } else {
        setState(prev => ({
          ...prev,
          initializing: false,
        }));
      }
    });

    // ============= RESILIÊNCIA: Recuperar sessão ao voltar à aba =============
    // Quando o dispositivo fica inativo (tela desligada, aba em background),
    // o auto-refresh pode falhar. Ao voltar, forçamos uma verificação.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        devLog('[Auth]', '👁️ Tab voltou ao foco, verificando sessão...');
        supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
          if (currentSession?.user) {
            // Sessão válida - garantir que o token está atualizado
            devLog('[Auth]', '✅ Sessão válida após retorno');
            // Forçar refresh do token para garantir que não expire logo
            supabase.auth.refreshSession().then(({ data: { session: refreshed } }) => {
              if (refreshed) {
                devLog('[Auth]', '🔄 Token renovado com sucesso');
              }
            }).catch(() => {
              devWarn('[Auth]', '⚠️ Falha ao renovar token, mas sessão ainda válida');
            });
          } else if (lastFetchedAuthIdRef.current) {
            // Tinha sessão antes mas perdeu - tentar recuperar
            devWarn('[Auth]', '⚠️ Sessão perdida durante inatividade');
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty dependency - runs only once

  const signIn = async (email: string, password: string) => {
    // Set loading state para evitar múltiplos cliques
    setAuthLoading(true);
    
    // Reset refs for new login attempt
    lastFetchedAuthIdRef.current = null;
    fetchingRef.current = false;
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      // Se login bem-sucedido e temos sessão, buscar perfil imediatamente
      if (!error && data.session?.user) {
        await fetchProfileAndRoles(data.session.user.id);
      }
      
      return { error: error as Error | null };
    } finally {
      setAuthLoading(false);
    }
  };

  const signOut = async (): Promise<{ error: Error | null }> => {
    devLog('[Auth]', 'signOut: starting...');
    
    try {
      // FIRST: Clear check-in state to prevent data leakage between users
      try {
        const appStoreModule = await import('@/stores/appStore');
        if (appStoreModule?.useAppStore?.getState) {
          const { resetCheckinParaNovoDia, resetarDia } = appStoreModule.useAppStore.getState();
          resetCheckinParaNovoDia?.();
          resetarDia?.();
          devLog('[Auth]', 'signOut: local state cleared');
        }
      } catch (storeError) {
        devWarn('[Auth]', 'signOut: Erro ao limpar store local:', storeError);
      }
      
      // CLEAR: Remove persisted Zustand localStorage to prevent stale data
      try {
        localStorage.removeItem('standfull-storage');
        // CLEAR: Limpar cache de status WhatsApp para evitar vazamento entre usuários
        Object.keys(localStorage)
          .filter(k => k.startsWith('wa_status_'))
          .forEach(k => localStorage.removeItem(k));
        devLog('[Auth]', 'signOut: localStorage standfull-storage e wa_status_* removidos');
      } catch (storageError) {
        devWarn('[Auth]', 'signOut: Erro ao limpar localStorage:', storageError);
      }
      
      // THEN: Sign out from Supabase
      devLog('[Auth]', 'signOut: calling supabase.auth.signOut...');
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        devError('[Auth]', 'signOut: Supabase error:', error);
        return { error };
      }
      
      devLog('[Auth]', 'signOut: Supabase signOut completed successfully');
      
      // Verify session is actually cleared
      const { data: sessionCheck } = await supabase.auth.getSession();
      if (sessionCheck?.session) {
        devWarn('[Auth]', 'signOut: Session still exists after signOut, forcing clear...');
        // Try global signout as fallback
        await supabase.auth.signOut({ scope: 'global' });
      }
      
      // FINALLY: Reset local auth state explicitly
      lastFetchedAuthIdRef.current = null;
      fetchingRef.current = false;
      setState({
        initializing: false,
        session: null,
        user: null,
        profile: null,
        roles: [],
        profileError: false,
      });
      
      devLog('[Auth]', 'signOut: complete, state reset');
      return { error: null };
    } catch (err) {
      devError('[Auth]', 'signOut: unexpected error:', err);
      // Even on error, try to clear local state
      lastFetchedAuthIdRef.current = null;
      fetchingRef.current = false;
      setState({
        initializing: false,
        session: null,
        user: null,
        profile: null,
        roles: [],
        profileError: false,
      });
      return { error: err as Error };
    }
  };

  const isAdmin = state.roles.includes('admin');
  const isOperador = state.roles.includes('operador');
  const isVendedor = state.roles.includes('vendedor');

  const value: AuthContextType = {
    ...state,
    isAdmin,
    isOperador,
    isVendedor,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
